import React, { useEffect } from "react";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import useTheme from "@material-ui/core/styles/useTheme";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton";
import { Typography } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField/TextField";
import { fetchUser } from "../../../actions/Management/userManagementActions";
import Avatar from "@material-ui/core/Avatar";
import CircularProgress from "@material-ui/core/CircularProgress";
import { sentenceCheck } from "../../../utils/sentenceCheck";
import { portalTokens as t } from "../../../theme/portalTokens";

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return createStyles({
    root: {
      marginTop: 60,
    },
    dialogPaper: {
      borderRadius: 16,
      border: `1px solid ${dark ? t.slate700 : t.slate200}`,
      overflow: "hidden",
      fontFamily: t.fontFamily,
      background: dark ? t.slate800 : t.white,
      color: dark ? t.slate100 : t.slate800,
      boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
    },
    closeButton: {
      position: "absolute",
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: dark ? t.slate300 : t.slate400,
      borderRadius: 10,
      "&:hover": {
        background: dark ? t.slate700 : t.slate100,
      },
      [theme.breakpoints.down("xs")]: {
        top: theme.spacing(1),
      },
    },
    avatar: {
      width: 96,
      height: 96,
      border: `2px solid ${dark ? t.slate600 : t.slate200}`,
    },
    title: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      fontFamily: t.fontFamily,
      fontWeight: 700,
      fontSize: 16,
      color: dark ? t.white : t.slate900,
      gap: 8,
    },
    textFields: {
      height: "auto",
      width: "100%",
      fontFamily: t.fontFamily,
      fontSize: 13,
      "& .MuiInputLabel-root": {
        fontSize: 12,
        fontWeight: 600,
        color: dark ? t.slate400 : t.slate500,
      },
      "& .MuiInputBase-root": {
        fontSize: 13,
        color: dark ? t.slate100 : t.slate800,
      },
    },
    sectionTitle: {
      fontSize: 13,
      fontWeight: 700,
      fontFamily: t.fontFamily,
      color: dark ? t.white : t.slate900,
      margin: "12px 0 8px",
    },
    settingsSection: {
      marginBottom: theme.spacing(1),
      fontFamily: t.fontFamily,
    },
  });
});

const UserDetail = ({ userId, onClose }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const intl = useIntl();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const user = useSelector((state) => state.userManagement.user);

  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [username, setUsername] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [phoneNumber, setPhoneNumber] = React.useState("");
  const [profilePicture, setProfilePicture] = React.useState(
    user?.profilePicture
  );
  const [birthDate, setBirthDate] = React.useState("");
  const [gender, setGender] = React.useState("");
  const [location, setLocation] = React.useState("");
  const [position, setPosition] = React.useState("");
  const [linkedinLink, setLinkedinLink] = React.useState("");
  const [instagramLink, setInstagramLink] = React.useState("");
  const [twitterLink, setTwitterLink] = React.useState("");

  useEffect(() => {
    fetchUserById();
  }, [userId]);

  useEffect(() => {
    if (user?.id) {
      setFirstName(user.firstName);
      setLastName(user.lastName);
      setUsername(user.username);
      setEmail(user.email);
      setPhoneNumber(user.phoneNumber);
      setProfilePicture(user.profilePicture);
      setBirthDate(user.birthDate);
      setGender(user.gender);
      setLocation(user.location);
      setPosition(user.position);
      setLinkedinLink(user.linkedinLink);
      setInstagramLink(user.instagramLink);
      setTwitterLink(user.twitterLink);
    }
  }, [user]);

  function fetchUserById() {
    dispatch(fetchUser(userId));
  }

  const handleOnClose = () => {
    onClose();
  };
  return (
    <div>
      {!userId || Object.entries(user).length ? (
        <Dialog
          open
          fullScreen={fullScreen}
          maxWidth="sm"
          fullWidth
          PaperProps={{ className: classes.dialogPaper }}
        >
          <DialogTitle
            id="responsive-dialog-title"
            style={{ display: "flex", justifyContent: "center" }}
          >
            <div className={classes.title}>
              {user.firstName && (
                <Avatar
                  className={classes.avatar}
                  displayName={[user.firstName, user.lastName].join(" ")}
                  src={profilePicture}
                  id={user?.id}
                />
              )}
              <span style={{ marginTop: 5 }}>
                {[
                  user.firstName,
                  user.lastName,
                  user.fromLdap ? " (LDAP)" : "",
                ].join(" ")}
              </span>
            </div>
          </DialogTitle>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={handleOnClose}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent>
            <div className={classes.settingsSection}>
              <Typography className={classes.sectionTitle}>
                <FormattedMessage
                  id="management.profile"
                  defaultMessage="Profile"
                />
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    id="firstName"
                    className={classes.textFields}
                    label={intl.formatMessage({
                      id: "management.user.firstname",
                      defaultMessage: "First Name",
                    })}
                    color="primary"
                    value={firstName}
                    variant="standard"
                    margin="normal"
                    InputProps={{
                      disableUnderline: true,
                      readOnly: true,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    id="lastName"
                    className={classes.textFields}
                    label={intl.formatMessage({
                      id: "management.user.lastname",
                      defaultMessage: "Last Name",
                    })}
                    color="primary"
                    value={lastName}
                    variant="standard"
                    margin="normal"
                    InputProps={{
                      disableUnderline: true,
                      readOnly: true,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                  />
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    id="birthDate"
                    className={classes.textFields}
                    label={intl.formatMessage({
                      id: "management.user.birthDate",
                      defaultMessage: "Birth Date",
                    })}
                    color="primary"
                    readOnly
                    value={birthDate ? birthDate : "-"}
                    variant="standard"
                    margin="normal"
                    readOnly
                    InputProps={{
                      disableUnderline: true,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    id="gender"
                    className={classes.textFields}
                    label={intl.formatMessage({
                      id: "management.user.gender",
                      defaultMessage: "Gender",
                    })}
                    color="primary"
                    value={gender ? gender : "-"}
                    variant="standard"
                    margin="normal"
                    readOnly
                    InputProps={{
                      disableUnderline: true,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                  ></TextField>
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    id="location"
                    className={classes.textFields}
                    label={intl.formatMessage({
                      id: "management.user.location",
                      defaultMessage: "Location",
                    })}
                    color="primary"
                    readOnly
                    value={location ? location : "-"}
                    variant="standard"
                    margin="normal"
                    readOnly
                    InputProps={{
                      disableUnderline: true,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    id="position"
                    className={classes.textFields}
                    label={intl.formatMessage({
                      id: "management.user.position",
                      defaultMessage: "Position",
                    })}
                    readOnly
                    color="primary"
                    value={position ? position : "-"}
                    variant="standard"
                    margin="normal"
                    InputProps={{
                      disableUnderline: true,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </div>

            <div className={classes.settingsSection}>
              <Typography className={classes.sectionTitle}>
                <FormattedMessage
                  id="management.account"
                  defaultMessage="Account"
                />
              </Typography>
              <Grid container spacing={3}>
                {true && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      id="username"
                      className={classes.textFields}
                      label={intl.formatMessage({
                        id: "management.user.username",
                        defaultMessage: "User Name",
                      })}
                      color="primary"
                      value={username}
                      variant="standard"
                      margin="normal"
                      InputProps={{
                        disableUnderline: true,
                        readOnly: true,
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                    />
                  </Grid>
                )}
              </Grid>
            </div>

            <div className={classes.settingsSection}>
              <Typography className={classes.sectionTitle}>
                <FormattedMessage
                  id="management.contactInfo"
                  defaultMessage="Contact Info"
                />
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    id="username"
                    className={classes.textFields}
                    label={intl.formatMessage({
                      id: "management.user.email",
                      defaultMessage: "Email",
                    })}
                    color="primary"
                    value={email}
                    variant="standard"
                    margin="normal"
                    InputProps={{
                      disableUnderline: true,
                      readOnly: true,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    id="phoneNumber"
                    className={classes.textFields}
                    label={intl.formatMessage({
                      id: "management.user.phoneNumber",
                      defaultMessage: "Phone Number",
                    })}
                    color="primary"
                    value={phoneNumber}
                    variant="standard"
                    margin="normal"
                    InputProps={{
                      disableUnderline: true,
                      readOnly: true,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </div>
          </DialogContent>
        </Dialog>
      ) : (
        <Dialog className={classes.dialog} open={true} fullScreen={fullScreen}>
          <DialogContent>
            <CircularProgress />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default UserDetail;
